<template>
  <v-container>
    <v-row>
      <v-col cols="auto">
        <v-icon
          size="large"
          :icon="CONSTANTS.ICONS_MDI.ICON_FILTER_OUTLINE"
          color="dark-cyan"
        ></v-icon>
      </v-col>
      <v-col align-self="center">
        <h2 class="itemis-analyze-title"> {{ labels.GLOBAL_FILTER.GLOBAL_FILTERS }} </h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="3" sm="6" md="4">
        <analyze-search v-model="search" :hide-details="true" />
      </v-col>
      <v-col>
        <v-btn
          id="create-new-filter-btn"
          color="cyan-dark"
          variant="flat"
          class="ml-5 create-filter-btn px-8"
          :prepend-icon="CONSTANTS.ICONS_MDI.ICON_EDIT_OUTLINE"
          @click="emit('enter-edit-mode')"
        >
          {{ labels.GLOBAL_FILTER.CREATE_NEW_GLOBAL_FILTER }}
        </v-btn>
      </v-col>
    </v-row>
    <div class="mt-16">
      <analyze-data-table
        v-if="filterItems.length > 0"
        id="global-filters-table"
        table-id="global-filters-table-id"
        :headers="filterHeaders"
        :fixed-header="true"
        :fixed-footer="true"
        :max-height="60"
        :items="filterItems"
        :has-apply-action="true"
        :has-delete-action="true"
        :search="search"
        :total-items="filterItems.length"
        data-type="filters"
        @apply="applyGlobalFilter"
        @delete="invokeDeletionDialog"
      />
      <v-row v-else>
        <v-col>
          <span id="empty-state" class="font-weight-medium ml-1">{{
            labels.GLOBAL_FILTER.LIST_EMPTY_STATE_LABEL
          }}</span>
        </v-col>
      </v-row>
    </div>
  </v-container>

  <ConfirmDeletionDialog
    v-if="selectedGlobalFilterToDelete"
    :key="`dialog-delete-global-filter${selectedGlobalFilterToDelete?.id}`"
    :element-to-delete="selectedGlobalFilterToDelete"
    :get-element-name="(p?: GlobalFilter) => p?.name ?? 'My Global Filter'"
    :element-type-name="labels.DELETE_ELEMENT.GLOBAL_FILTTER"
    :is-deleting-element="isDeletingGlobalFilter"
    :extra-confirmation-required="true"
    @confirm-deletion="deleteGlobalFilter"
    @cancel-deletion="selectedGlobalFilterToDelete = undefined"
  />
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { ref } from 'vue'
  import { useRoute } from 'vue-router'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { GlobalFilter } from '@/models'
  import { useProjectStore } from '@/stores/projectStore'
  import { storeToRefs } from 'pinia'
  import { parseIntUndefined } from '@/utils/helpers'
  const globalFilterStore = useGlobalFilterStore()
  const { currentProject } = storeToRefs(useProjectStore())

  const route = useRoute()
  const search = ref('')
  const selectedGlobalFilterToDelete = ref<GlobalFilter | undefined>()
  const isDeletingGlobalFilter = ref(false)

  const emit = defineEmits<{
    (event: 'enter-edit-mode'): void
  }>()

  const filterHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'selected', width: '58' },
    { title: 'Name', key: 'name' },
    { title: '', key: 'actions', align: 'end', sortable: false },
  ])

  const filterItems = computed((): AnalyzeTableItem<GlobalFilter>[] =>
    globalFilterStore.globalFilters.map((globalFilter) => ({
      name: globalFilter.name,
      value: globalFilter,
      selected: globalFilter.id === globalFilterStore.selectedGlobalFilterId,
    })),
  )

  function applyGlobalFilter(filter?: GlobalFilter): void {
    if (filter) {
      globalFilterStore.applyGlobalFilter(filter)
    }
  }

  function invokeDeletionDialog(item?: GlobalFilter): void {
    if (item) {
      selectedGlobalFilterToDelete.value = item
    }
  }

  async function deleteGlobalFilter(): Promise<void> {
    if (!currentProject.value || !selectedGlobalFilterToDelete.value) {
      return Promise.resolve()
    }

    isDeletingGlobalFilter.value = true

    if (
      selectedGlobalFilterToDelete.value &&
      selectedGlobalFilterToDelete.value.id === globalFilterStore.selectedGlobalFilterId
    ) {
      globalFilterStore.applyGlobalFilter(undefined)
    }

    await globalFilterStore.deleteGlobalFilter(
      currentProject.value.id,
      selectedGlobalFilterToDelete.value,
    )

    isDeletingGlobalFilter.value = false
    selectedGlobalFilterToDelete.value = undefined
  }

  watch(
    currentProject,
    (project) => {
      if (project) {
        globalFilterStore.loadGlobalFiltersFromApi(
          project.id,
          parseIntUndefined(route.query.filter as string),
        )
      }
    },
    { immediate: true },
  )
</script>
